import { useQuery } from "@tanstack/react-query";
import { apiClient, apiErrorMessage } from "../api/client";
import type { EntityName } from "../api/types";
import { shortDate } from "../lib/format";
import { Fact } from "../ui/Facts";
import { Spinner } from "./Spinner";

/* Every other name a company or a person is known by, from engine migration
   119. A name comes from a record merged into this one or from before a
   rename, from a source that named this record, or from a vendor file. Each
   shows when it was first seen and, for a source, the page that used it, so
   where a name came from is always reachable. The record's current name is
   left out.

   On the Details tab this is one fact, "Also known as", one name per line. */

const FROM: Record<string, string> = { recorded: "merged or old name", seen: "source", vendor: "vendor" };
const LIMIT = 50;

export function OtherNamesFact({ token, entityType, entityId, currentName }: {
  token: string; entityType: "company" | "person"; entityId: string; currentName: string | null | undefined;
}) {
  const names = useQuery({
    queryKey: ["other-names", entityType, entityId],
    queryFn: async () => {
      const { data, error } = await apiClient(token)
        .from("entity_name")
        .select("name,source,first_seen,seen_count,source_url")
        .eq("entity_type", entityType).eq("entity_id", entityId)
        .order("seen_count", { ascending: false }).order("name", { ascending: true })
        .limit(LIMIT);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as EntityName[];
    },
  });

  // One line per spelling. The lake keeps a row per record a name was seen
  // on, so a name merged in from three records arrives three times.
  const current = (currentName ?? "").trim().toLowerCase();
  const seen = new Set<string>([current]);
  const rows = (names.data ?? []).filter((n) => {
    const k = (n.name ?? "").trim().toLowerCase();
    if (!k || seen.has(k)) return false;
    seen.add(k);
    return true;
  });

  return (
    <Fact label="Also known as">
      {names.isLoading ? <Spinner />
        : names.isError ? <span className="refusal">{(names.error as Error).message}</span>
        : !rows.length ? <span className="muted">no other names on record</span>
        : (
          <ul className="fact-lines">
            {rows.map((n) => {
              const from = FROM[n.source ?? ""] ?? n.source;
              return (
                <li key={n.name ?? ""}>
                  {n.name}
                  <span className="muted">
                    {" · "}
                    {n.source_url ? <a href={n.source_url} target="_blank" rel="noreferrer">{from}</a> : from}
                    {n.first_seen ? <>, first seen {shortDate(n.first_seen)}</> : null}
                    {(n.seen_count ?? 1) > 1 ? <>, seen {n.seen_count} times</> : null}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
    </Fact>
  );
}
